// components/ListGrupos
import React, { useState, useEffect, useCallback } from "react";
// Importar useNavigate para manejar la navegación
import { useNavigate } from "react-router-dom";
import "./ListGrupos.css";
import GroupCard from "../../modules/groups/GroupCard/GroupCard";
import { getAuthHeaders } from "../../utils/apiUtils"; // Ajusta la ruta si es necesario

// Se asume que este archivo existe y contiene la URL base de tu API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Componente auxiliar para mostrar el estado de carga
const LoadingState = () => (
  <div className="text-center my-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
    <p className="mt-3">Cargando grupos recientes y populares...</p>
  </div>
);

const CardListGroup = () => {
  const navigate = useNavigate(); // Hook para la navegación
  const [searchTerm, setSearchTerm] = useState("");
  const [groups, setGroups] = useState([]); // Estado para almacenar los grupos de la API
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Función que realiza la petición a la API, memorizada con useCallback
  const fetchGroups = useCallback(async (query = "") => {
    setIsLoading(true);
    setApiError(null);

    const token = getAuthHeaders();
    if (!token) {
      setApiError("Error de autenticación: Por favor, inicia sesión.");
      setIsLoading(false);
      return;
    }

    try {
      const url = `${API_BASE_URL}/creargrupo/lista?q=${encodeURIComponent(
        query
      )}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Capturar errores 4xx/5xx del backend
        throw new Error(data.message || "Error al obtener la lista de grupos.");
      }

      // Actualizar el estado con los datos reales del API
      setGroups(data);
    } catch (error) {
      console.error("Error al cargar grupos:", error);
      setApiError(error.message);
      setGroups([]); // Limpiar grupos si hay un error
    } finally {
      setIsLoading(false);
    }
  }, []); // El useCallback no tiene dependencias ya que usa estados y constantes

  // Efecto que se dispara al montar el componente y cada vez que cambia el searchTerm
  // Usamos un pequeño debounce implícito o un submit para evitar peticiones en cada tecla.
  // Para simplificar, aquí usaremos el useEffect disparado por el cambio de `searchTerm` (al escribir):
  useEffect(() => {
    // Opcional: Si quieres esperar a que el usuario deje de escribir (debounce),
    // puedes implementarlo aquí. Por ahora, hacemos la petición inmediatamente:
    const handler = setTimeout(() => {
      fetchGroups(searchTerm);
    }, 300); // Espera 300ms después de la última pulsación para buscar

    return () => {
      clearTimeout(handler); // Limpia el timeout si el componente o la búsqueda cambia
    };
  }, [searchTerm, fetchGroups]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // =======================================================
  // NUEVA LÓGICA DE NAVEGACIÓN (Ver Detalle)
  // =======================================================
  const handleViewDetail = (sharedId) => {
    // Navega a la ruta de detalle del grupo, enviando el ID hasheado.
    // La pantalla de destino (ej: GroupDetailScreen) se encargará de deshashear.
    navigate(`/groups/${sharedId}/detalle`);
  };

  // =======================================================
  // NUEVA LÓGICA DE NAVEGACIÓN (Administrar)
  // =======================================================
  const handleAdminGroup = (sharedId) => {
    // Navega a la ruta de administración/edición del grupo.
    navigate(`/groups/${sharedId}/admin`);
  };

  // =======================================================
  // Manejo de Unirse/Salir (Placeholder)
  // =======================================================
  const handleJoinLeaveGroup = (sharedId, action) => {
    console.log(`Acción pendiente: ${action} en grupo ID: ${sharedId}`);
    // Aquí iría la llamada a la API para unirse o salir
  };

  // No necesitamos la lógica de filtrado local, ya que la API (backend) lo maneja

  const renderGroupContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (apiError) {
      return (
        <div className="alert alert-danger text-center my-5" role="alert">
          ⚠️ Error: {apiError}
        </div>
      );
    }

    if (groups.length === 0 && searchTerm === "") {
      return (
        <div className="alert alert-info text-center my-5" role="alert">
          No se encontraron grupos. Sé el primero en crear uno.
        </div>
      );
    }

    if (groups.length === 0 && searchTerm !== "") {
      return (
        <div className="alert alert-warning text-center my-5" role="alert">
          No se encontraron grupos que coincidan con la búsqueda: **"
          {searchTerm}"**.
        </div>
      );
    }

    return (
      <div className="row">
        {groups.map((group) => (
          <div
            key={group.id} // Usamos el ID de grupo único de la API
            className="col-12 col-sm-6 col-lg-6 mb-4"
          >
            {/* GroupCard recibirá la data del backend, incluyendo:
                            - tags: ['I', 'A', 'E'] (Iniciales de los 3 últimos inscritos)
                            - Propietario: true/false
                        */}
            <GroupCard
              // Pasamos el ID hasheado provisto por el backend
              sharedGroupId={group.id}
              title={group.title}
              description={group.description}
              tagsMembers={group.tagsMembers}
              members={group.memberCount}
              tipoGrupo={group.tipoGrupo}
              imageUrl={group.imageUrl}
              isOwner={group.Propietario} // Pasar la propiedad del dueño
              isMember={group.Propietario} // Asumo que el API devuelve si el usuario es miembro
              // Handlers que implementan la navegación con el ID hasheado
              onViewGroupDetail={handleViewDetail}
              onAdminGroup={handleAdminGroup}
              onJoinLeaveGroup={handleJoinLeaveGroup}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container mt-5">
      {/* Barra de Búsqueda */}
      <div className="row mb-5 justify-content-center">
        <div className="col-12 col-lg-8">
          <div className="input-group search-input-container">
            <span className="input-group-text search-icon-bg">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Palabra clave del grupo"
              value={searchTerm}
              // Al escribir, actualizamos el estado. El useEffect se encarga de llamar a la API.
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Título de la Sección */}
      <h2 className="group-list-title mb-4">Conecta con Grupos</h2>

      {/* Lista de Grupos o Mensajes de Estado */}
      {renderGroupContent()}
    </div>
  );
};

export default CardListGroup;
